import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../client/query-client';
import { CaseStudy } from '../cases/cases';
import { AgeBand, Audio, Collection, Course, Destination, Podcast, PodcastSeries, Printable, QandA, Reading, localisedOf, } from './contracts';
const paged = (item) => z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    locale: z.string(),
});
/**
 * The API's own path segment → the schema its rows carry.
 *
 * A full record, so adding a segment fails to compile until somebody says what
 * it returns — the same discipline the backend's content mediator uses, and the
 * reason there is no `contentResource()` factory beside this: one way to read a
 * content list, not two.
 */
const READERS = {
    readings: Reading,
    audios: Audio,
    printables: Printable,
    collections: Collection,
    courses: Course,
    'case-studies': CaseStudy,
    podcasts: Podcast,
    qanda: QandA,
};
/**
 * The subset the LIBRARY filters over — deliberately narrower.
 *
 * Case studies are readable through the same list surface but are not a Library
 * filter: they are their own nav destination for Pro and TSP, and putting them
 * behind a type pill in the parent Library would offer a chip that is empty for
 * the audience most likely to click it.
 *
 * Two names because they are two questions. Collapsing them made adding a
 * readable type silently add a Library pill, which is how a filter appears in
 * a product nobody designed it into.
 *
 * **Audio is readable but not a Library segment** (operator, 14 Sep 2026): the
 * catalogue's audio pieces stay reachable from Home, search and their own
 * links, but the Library offers no Audio pill, shelf or format tile.
 *
 * **Podcasts took the collections' place** (operator, 18 Sep 2026): the
 * episodes moved out of their own nav entry into the Library, where the same
 * destination, age and search filters apply to them (`GET /podcasts` takes
 * all three). A collection is still readable by its link; it is no longer a
 * pill or a shelf.
 */
export const LIBRARY_SEGMENTS = [
    'courses',
    'readings',
    'printables',
    'podcasts',
];
/**
 * One list hook for every type, because the API has one list surface.
 *
 * `segment` is the API's own `CONTENT_PATH` value, not our route — the backend
 * refuses to mint links into our routing and we return the favour by not
 * assuming theirs matches ours.
 */
export function useContentList(http, segment, filters, locale) {
    /*
     * The cast is the price of one hook over four. `READERS[segment]` is a union
     * to the compiler even when `segment` is narrowed by the caller, so the
     * inferred row type widens to the union of all four. The cast restores what
     * the caller already knows; the runtime schema is still the right one,
     * because it is indexed by the same key.
     */
    const schema = paged(READERS[segment]);
    return useQuery({
        queryKey: queryKeys.content.list(segment, filters, locale),
        queryFn: () => http.get(`/${segment}`, { query: { ...filters }, schema, locale }),
    });
}
export function useContentItem(http, segment, id, locale) {
    const schema = READERS[segment];
    return useQuery({
        queryKey: queryKeys.content.one(segment, id ?? '', locale),
        enabled: Boolean(id),
        queryFn: () => http.get(`/${segment}/${id}`, { schema, locale }),
    });
}
/**
 * The podcast series, for grouping the gallery.
 *
 * `visible: false` is an editorial state and the list is not filtered on it
 * here — the API decides what a member may list, and second-guessing it in the
 * client is how two definitions of "visible" end up disagreeing.
 */
export function usePodcastSeries(http, locale) {
    return useQuery({
        queryKey: ['podcast-series', locale],
        queryFn: () => 
        // The `localised` envelope, NOT the paged one — series are unpaged.
        http.get('/podcast-series', { schema: localisedOf(PodcastSeries), locale }),
        staleTime: 10 * 60_000,
    });
}
/**
 * The destination vocabulary for ONE audience.
 *
 * ── `audience` is required, because the unfiltered list is now wrong ──────
 * Until 2026-08-19 there was one vocabulary and every caller wanted all of it.
 * There are now practitioner terms — *Clinical integration*, *Case
 * formulation* — and an unfiltered read returns them to everybody, so a
 * parent's filter row would offer to narrow their library by a term from
 * clinical supervision.
 *
 * Required rather than defaulted for that reason: a default is a call site
 * that can forget, and the failure is silent and only visible to the audience
 * that should never have seen the term. `undefined` holds the query until the
 * session resolves rather than asking for the wrong list.
 *
 * A practitioner's answer INCLUDES the unrestricted parent terms — a Pro
 * reading about sleep is legitimate — which is the server's decision, not
 * ours to re-make here.
 */
export function useDestinations(http, locale, audience) {
    return useQuery({
        queryKey: ['taxonomy', 'destinations', locale, audience],
        enabled: Boolean(audience),
        queryFn: () => http.get('/destinations', {
            query: { audience: audience, pageSize: 100 },
            schema: localisedOf(Destination),
            locale,
        }),
        staleTime: 30 * 60_000,
    });
}
export function useAgeBands(http, locale) {
    return useQuery({
        queryKey: ['taxonomy', 'age-bands', locale],
        queryFn: () => http.get('/age-bands', { schema: localisedOf(AgeBand), locale }),
        staleTime: 30 * 60_000,
    });
}
//# sourceMappingURL=library.js.map